import _ from 'underscore';
import BaseExporter from './BaseExporter';
import TimecodeConverter from '../utils/TimecodeConverter';
import ti18n from 'i18n!nls/ExportPopupi18n';

/**
 * PremiereXmlExporter - Export timeline data to Adobe Premiere XML format
 * 
 * Features:
 * - XMEML v5 format (Better compatibility across versions)
 * - Multi-track support with separate tracks per source file
 * - Direct sequence structure without bin/master clips
 * - Frame-accurate timing with NTSC/PAL support
 * - Media file references with proper paths
 */
const PremiereXmlExporter = BaseExporter.extend({

    /**
     * Export timeline data as Adobe Premiere XML format
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options  
     * @returns {String} Premiere XML content as XML
     */
    export: function(timelineData, options) {
        options = this.validateAndExtendOptions(options);
        this.validateTimelineData(timelineData);

        try {
            // Generate XMEML header (Version 5 for better compatibility)
            let xmlContent = `<?xml version="1.0" encoding="UTF-8"?>\n`;
            xmlContent += `<xmeml version="5">\n`;
            xmlContent += `    <sequence>\n`;
            xmlContent += `        <name>${this.escapeXml(options.projectTitle)}</name>\n`;
            xmlContent += `        <duration>${Math.round(this.getTotalDuration(timelineData) * options.frameRate)}</duration>\n`;
            xmlContent += `        <rate>\n`;
            // Get correct timebase and NTSC settings for frame rate
            const timebaseInfo = this._getTimebaseForFrameRate(options.frameRate);
            xmlContent += `            <timebase>${timebaseInfo.timebase}</timebase>\n`;
            xmlContent += `            <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
            xmlContent += `        </rate>\n`;
            xmlContent += `        <in>-1</in>\n`;
            xmlContent += `        <out>-1</out>\n`;
            xmlContent += `        <timecode>\n`;
            xmlContent += `            <string>00:00:00:00</string>\n`;
            xmlContent += `            <frame>0</frame>\n`;
            const displayFormat = TimecodeConverter.shouldUseDropFrame(options.frameRate) ? 'DF' : 'NDF';
            xmlContent += `            <displayformat>${displayFormat}</displayformat>\n`;
            xmlContent += `            <rate>\n`;
            xmlContent += `                <timebase>${timebaseInfo.timebase}</timebase>\n`;
            xmlContent += `                <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
            xmlContent += `            </rate>\n`;
            xmlContent += `        </timecode>\n`;
            xmlContent += `        <media>\n`;
            
            // Generate video and audio tracks
            xmlContent += this._generateVideoSection(timelineData, options);
            xmlContent += this._generateAudioSection(timelineData, options);
            
            xmlContent += `        </media>\n`;
            xmlContent += `    </sequence>\n`;
            xmlContent += `</xmeml>\n`;

            return xmlContent;

        } catch (error) {
            console.error('Error generating Adobe Premiere XML:', error);
            throw this.createExportError(window.getI18n(ti18n, 'EXPORT_FAILED'), error);
        }
    },

    /**
     * Generate video section with tracks
     * @private
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} Video section XML content
     */
    _generateVideoSection: function(timelineData, options) {
        // Filter for video clips
        const videoClips = timelineData.filter(clip => clip.hasVideo);
        if (videoClips.length === 0) {
            return '';
        }

        let videoXml = `            <video>\n`;
        
        // Create video tracks
        const trackGroups = this.createTrackGroups(videoClips, options);
        trackGroups.forEach(trackGroup => {
            videoXml += `                <track>\n`;
            
            // Add clips from this source to this track
            trackGroup.clips.forEach(clip => {
                videoXml += this._generateVideoClipItem(clip, options);
            });
            
            videoXml += `                </track>\n`;
        });
        
        // Add video format section like the working competitor
        const timebaseInfo = this._getTimebaseForFrameRate(options.frameRate);
        videoXml += `                <format>\n`;
        videoXml += `                    <samplecharacteristics>\n`;
        videoXml += `                        <width>1920</width>\n`;
        videoXml += `                        <height>1080</height>\n`;
        videoXml += `                        <pixelaspectratio>square</pixelaspectratio>\n`;
        videoXml += `                        <rate>\n`;
        videoXml += `                            <timebase>${timebaseInfo.timebase}</timebase>\n`;
        videoXml += `                            <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
        videoXml += `                        </rate>\n`;
        videoXml += `                    </samplecharacteristics>\n`;
        videoXml += `                </format>\n`;
        
        videoXml += `            </video>\n`;
        return videoXml;
    },

    /**
     * Generate audio section with tracks
     * @private
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} Audio section XML content
     */
    _generateAudioSection: function(timelineData, options) {
        let audioXml = `            <audio>\n`;
        
        // Filter clips to include only those with audio content
        const audioClips = timelineData.filter(clip => !clip.hasVideo || clip.hasAudio !== false);
        
        // Create audio tracks
        const trackGroups = this.createTrackGroups(audioClips, options);
        trackGroups.forEach(trackGroup => {
            audioXml += `                <track>\n`;
            
            // Add clips from this source to this track
            trackGroup.clips.forEach(clip => {
                audioXml += this._generateAudioClipItem(clip, options, trackGroup.trackNumber);
            });
            
            audioXml += `                </track>\n`;
        });
        
        audioXml += `            </audio>\n`;
        return audioXml;
    },

    /**
     * Generate video clip item XML for v5 format
     * @private
     * @param {Object} clip - Timeline clip object
     * @param {Object} options - Export options
     * @returns {String} Video clip item XML
     */
    _generateVideoClipItem: function(clip, options) {
        const startFrame = Math.round(clip.startTimeInSeconds * options.frameRate);
        const endFrame = Math.round(clip.endTimeInSeconds * options.frameRate);
        const durationFrames = Math.round(clip.durationInSeconds * options.frameRate);
        const sourceInFrames = Math.round(clip.sourceStartInSeconds * options.frameRate);
        const sourceOutFrames = Math.round(clip.sourceEndInSeconds * options.frameRate);
        const sourceFilePath = this.getMediaFilePath(clip, options);
        const timebaseInfo = this._getTimebaseForFrameRate(options.frameRate);
        const fileName = this.getClipFileName(clip);
        const fileDurationFrames = Math.max(durationFrames, sourceOutFrames);
        
        const uniqueId = this._generateUniqueId(this.getClipMediaKey(clip), startFrame);
        let clipXml = `                    <clipitem id="${uniqueId}">\n`;
        clipXml += `                        <name>${this.escapeXml(fileName)}</name>\n`;
        clipXml += `                        <duration>${durationFrames}</duration>\n`;
        clipXml += `                        <rate>\n`;
        clipXml += `                            <timebase>${timebaseInfo.timebase}</timebase>\n`;
        clipXml += `                            <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
        clipXml += `                        </rate>\n`;
        clipXml += `                        <start>${startFrame}</start>\n`;
        clipXml += `                        <end>${endFrame}</end>\n`;
        clipXml += `                        <enabled>true</enabled>\n`;
        clipXml += `                        <in>${sourceInFrames}</in>\n`;
        clipXml += `                        <out>${sourceOutFrames}</out>\n`;
        clipXml += `                        <file id="${uniqueId}-video">\n`;
        clipXml += `                            <duration>${fileDurationFrames}</duration>\n`;
        clipXml += `                            <rate>\n`;
        clipXml += `                                <timebase>${timebaseInfo.timebase}</timebase>\n`;
        clipXml += `                                <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
        clipXml += `                            </rate>\n`;
        clipXml += `                            <name>${this.escapeXml(fileName)}</name>\n`;
        clipXml += `                            <pathurl>${this.escapeXml(sourceFilePath)}</pathurl>\n`;
        clipXml += `                            <timecode>\n`;
        clipXml += `                                <string>00:00:00:00</string>\n`;
        clipXml += `                                <frame>0</frame>\n`;
        clipXml += `                                <displayformat>${TimecodeConverter.shouldUseDropFrame(options.frameRate) ? 'DF' : 'NDF'}</displayformat>\n`;
        clipXml += `                                <rate>\n`;
        clipXml += `                                    <timebase>${timebaseInfo.timebase}</timebase>\n`;
        clipXml += `                                    <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
        clipXml += `                                </rate>\n`;
        clipXml += `                            </timecode>\n`;
        clipXml += `                            <media>\n`;
        clipXml += `                                <video>\n`;
        clipXml += `                                    <duration>${fileDurationFrames}</duration>\n`;
        clipXml += `                                    <samplecharacteristics>\n`;
        clipXml += `                                        <width>${clip.videoWidth || 1280}</width>\n`;
        clipXml += `                                        <height>${clip.videoHeight || 720}</height>\n`;
        clipXml += `                                    </samplecharacteristics>\n`;
        clipXml += `                                </video>\n`;
        clipXml += `                                \n`;
        clipXml += `                            </media>\n`;
        clipXml += `                        </file>\n`;
        clipXml += `                        <compositemode>normal</compositemode>\n`;
        clipXml += `                        <link>\n`;
        clipXml += `                            <linkclipref>${this.escapeXml(fileName)}</linkclipref>\n`;
        clipXml += `                        </link>\n`;
        clipXml += `                        <comments></comments>\n`;
        clipXml += `                    </clipitem>\n`;
        
        return clipXml;
    },

    /**
     * Generate audio clip item XML for v5 format
     * @private
     * @param {Object} clip - Timeline clip object
     * @param {Object} options - Export options
     * @param {Number} trackIndex - Source track index (1-based)
     * @returns {String} Audio clip item XML
     */
    _generateAudioClipItem: function(clip, options, trackIndex = 1) {
        const startFrame = Math.round(clip.startTimeInSeconds * options.frameRate);
        const endFrame = Math.round(clip.endTimeInSeconds * options.frameRate);
        const durationFrames = Math.round(clip.durationInSeconds * options.frameRate);
        const sourceInFrames = Math.round(clip.sourceStartInSeconds * options.frameRate);
        const sourceOutFrames = Math.round(clip.sourceEndInSeconds * options.frameRate);
        const sourceFilePath = this.getMediaFilePath(clip, options);
        const timebaseInfo = this._getTimebaseForFrameRate(options.frameRate);
        const fileName = this.getClipFileName(clip);
        const fileDurationFrames = Math.max(durationFrames, sourceOutFrames);
        
        // Generate unique ID based on content like the working competitor
        const uniqueId = this._generateUniqueId(this.getClipMediaKey(clip), startFrame);
        
        let clipXml = `                    <clipitem id="${uniqueId}-losslessAudio">\n`;
        clipXml += `                        <name>${this.escapeXml(fileName)}</name>\n`;
        clipXml += `                        <duration>${durationFrames}</duration>\n`;
        clipXml += `                        <rate>\n`;
        clipXml += `                            <timebase>${timebaseInfo.timebase}</timebase>\n`;
        clipXml += `                            <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
        clipXml += `                        </rate>\n`;
        clipXml += `                        <start>${startFrame}</start>\n`;
        clipXml += `                        <end>${endFrame}</end>\n`;
        clipXml += `                        <enabled>true</enabled>\n`;
        clipXml += `                        <in>${sourceInFrames}</in>\n`;
        clipXml += `                        <out>${sourceOutFrames}</out>\n`;
        clipXml += `                        <file id="${uniqueId}-losslessAudio">\n`;
        clipXml += `                            <duration>${fileDurationFrames}</duration>\n`;
        clipXml += `                            <rate>\n`;
        clipXml += `                                <timebase>${timebaseInfo.timebase}</timebase>\n`;
        clipXml += `                                <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
        clipXml += `                            </rate>\n`;
        clipXml += `                            <name>${this.escapeXml(fileName)}</name>\n`;
        clipXml += `                            <pathurl>${this.escapeXml(sourceFilePath)}</pathurl>\n`;
        clipXml += `                            <timecode>\n`;
        clipXml += `                                <string>00:00:00:00</string>\n`;
        clipXml += `                                <frame>0</frame>\n`;
        clipXml += `                                <displayformat>${TimecodeConverter.shouldUseDropFrame(options.frameRate) ? 'DF' : 'NDF'}</displayformat>\n`;
        clipXml += `                                <rate>\n`;
        clipXml += `                                    <timebase>${timebaseInfo.timebase}</timebase>\n`;
        clipXml += `                                    <ntsc>${timebaseInfo.ntsc}</ntsc>\n`;
        clipXml += `                                </rate>\n`;
        clipXml += `                            </timecode>\n`;
        clipXml += `                            <media>\n`;
        clipXml += `                                <audio>\n`;
        clipXml += `                                    <channelcount>1</channelcount>\n`;
        clipXml += `                                </audio>\n`;
        clipXml += `                            </media>\n`;
        clipXml += `                        </file>\n`;
        clipXml += `                        <sourcetrack>\n`;
        clipXml += `                            <mediatype>audio</mediatype>\n`;
        clipXml += `                            <trackindex></trackindex>\n`;
        clipXml += `                        </sourcetrack>\n`;
        clipXml += `                        <link>\n`;
        clipXml += `                            <linkclipref>${this.escapeXml(fileName)}</linkclipref>\n`;
        clipXml += `                        </link>\n`;
        clipXml += `                        <link>\n`;
        clipXml += `                            <linkclipref>${this.escapeXml(fileName)}</linkclipref>\n`;
        clipXml += `                        </link>\n`;
        clipXml += `                        <comments></comments>\n`;
        clipXml += `                    </clipitem>\n`;
        
        return clipXml;
    },


    /**
     * Generate unique ID for clips based on filename and timing
     * @private
     * @param {String} fileName - Source filename
     * @param {Number} startFrame - Start frame number
     * @returns {String} Unique ID string
     */
    _generateUniqueId: function(fileName, startFrame) {
        // Generate a hash-like ID similar to the competitor's format
        const baseString = fileName + startFrame.toString();
        let hash = 0;
        for (let i = 0; i < baseString.length; i++) {
            const char = baseString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        // Convert to hex and ensure it's 24 characters like competitor
        const hexHash = Math.abs(hash).toString(16).padStart(8, '0');
        return '68438775e65c3c' + hexHash.slice(0, 10);
    },

    /**
     * Convert seconds to frame count
     * @private
     * @param {Number} seconds - Time in seconds
     * @param {Number} frameRate - Frame rate
     * @returns {Number} Frame count
     */
    _convertSecondsToFrames: function(seconds, frameRate) {
        return Math.round(seconds * frameRate);
    },

    /**
     * Get timebase information for frame rate
     * @private
     * @param {Number} frameRate - Target frame rate
     * @returns {Object} Timebase configuration
     */
    _getTimebaseForFrameRate: function(frameRate) {
        // Map frame rates to Premiere timebase settings
        const timebaseMap = {
            23.976: { timebase: 24, ntsc: 'TRUE' },
            24: { timebase: 24, ntsc: 'FALSE' },
            25: { timebase: 25, ntsc: 'FALSE' },
            29.97: { timebase: 30, ntsc: 'TRUE' },
            30: { timebase: 30, ntsc: 'FALSE' },
            50: { timebase: 50, ntsc: 'FALSE' },
            59.94: { timebase: 60, ntsc: 'TRUE' },
            60: { timebase: 60, ntsc: 'FALSE' }
        };
        
        return timebaseMap[frameRate] || { timebase: 25, ntsc: 'FALSE' }; // Default to 25fps PAL
    },

    /**
     * Get supported Premiere XML features
     * @returns {Object} Supported features and capabilities
     */
    getSupportedFeatures: function() {
        return {
            version: '5',
            supports: {
                video: true,
                audio: true,
                multiTrack: true,
                timecode: true,
                bins: false,       // v5 format doesn't use bins
                masterClips: false, // v5 format doesn't use master clips
                transitions: false, // Basic clips only for now
                effects: false,     // Basic clips only for now
                titles: false       // Basic clips only for now
            },
            frameRates: [23.976, 24, 25, 29.97, 30, 50, 59.94, 60],
            resolutions: ['1920x1080', '1280x720', '3840x2160'],
            audioFormats: ['48kHz/16-bit', '44.1kHz/16-bit']
        };
    },

    /**
     * Validate Premiere XML-specific options
     * @param {Object} options - Export options
     * @returns {Object} Validated options
     */
    validateAndExtendOptions: function(options) {
        const validatedOptions = BaseExporter.prototype.validateAndExtendOptions.call(this, options);
        
        // Ensure frame rate is supported
        const supportedFrameRates = this.getSupportedFeatures().frameRates;
        if (!supportedFrameRates.includes(validatedOptions.frameRate)) {
            // Find closest supported frame rate
            validatedOptions.frameRate = TimecodeConverter.getClosestStandardFrameRate(validatedOptions.frameRate);
        }
        
        return validatedOptions;
    },

    /**
     * Override getMediaFilePath to handle both loaded and non-loaded project contexts
     * Similar to FCPXML, but optimized for Premiere Pro workflows
     * @param {Object} clip - Timeline clip object
     * @param {Object} options - Export options
     * @returns {String} Media file path
     */
    getMediaFilePath: function(clip, options) {
        const sourceUrl = clip.sourceMediaUrl;
        const fileName = this.getClipFileName(clip);
        
        // For Premiere Pro, we want local file paths pointing to the media folder
        // Files are packaged in a 'media' subdirectory
        
        // If we have a filename, use it with media/ prefix
        if (fileName && fileName !== 'unknown_file') {
            return 'media/' + fileName;
        }
        
        // If we have an HTTP URL, extract just the filename and add media/ prefix
        if (sourceUrl && sourceUrl.startsWith('http')) {
            const urlParts = sourceUrl.split('/');
            const filename = urlParts[urlParts.length - 1];
            if (filename && filename.includes('.')) {
                return 'media/' + filename;
            }
        }
        
        // If we have a blob URL (editor context), use filename if available
        if (sourceUrl && sourceUrl.startsWith('blob:')) {
            if (fileName && fileName !== 'unknown_file') {
                return 'media/' + fileName;
            }
        }
        
        // If we have a local file path, add media/ prefix
        if (sourceUrl && !sourceUrl.includes('blob:') && !sourceUrl.startsWith('http') && sourceUrl.length > 0) {
            // Extract just the filename from the path
            const pathParts = sourceUrl.split('/');
            const filename = pathParts[pathParts.length - 1];
            return 'media/' + filename;
        }
        
        // Last resort: Generate a descriptive filename with media/ prefix
        const timeBasedName = `trebble_clip_${Math.round(clip.startTimeInSeconds)}_${Math.round(clip.durationInSeconds)}s.${clip.hasVideo ? 'mp4' : 'mp3'}`;
        return 'media/' + timeBasedName;
    },

    /**
     * Get Premiere XML-specific default options
     * @returns {Object} Premiere format defaults
     */
    getDefaultOptions: function() {
        return _.extend(BaseExporter.prototype.getDefaultOptions.call(this), {
            frameRate: 25,
            projectTitle: 'Trebble Premiere Project',
            mediaPathType: 'relative', // Premiere works well with relative paths
            startTimecode: '00:00:00:00'
        });
    },

    /**
     * Generate Premiere XML project info
     * @param {Object} options - Export options (optional)
     * @returns {Object} Project information for Premiere XML
     */
    getProjectInfo: function(options) {
        options = options || {};

        return {
            format: `XMEML v5`,
            frameRate: options.frameRate,
            timebase: options.frameRate,
            ntsc: false,
            version: '5',
            compatible: ['Adobe Premiere Pro', 'Adobe After Effects', 'DaVinci Resolve (limited)'],
            features: [
                'Multi-track timeline',
                'Direct sequence structure',
                'Frame-accurate timing',
                'Audio/video synchronization',
                'Better cross-platform compatibility'
            ]
        };
    }

});

export default PremiereXmlExporter;
